/**
 * PDF EXPORT ENGINE
 *
 * Generates a clean, professional printable PDF of the training plan
 * using jsPDF + jspdf-autotable.
 * Includes Roboto font for Polish character support.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Plan, Week, VolumeMetrics } from '../types';
import { formatRest, formatLoad, parseReps, volumeStatusLabel } from './helpers';
import { MUSCLE_GROUP_LABELS, TRAINING_GOAL_LABELS, TRAINING_LEVEL_LABELS, PROGRESSION_MODEL_LABELS, EXERCISE_TAG_LABELS } from '../types';
import { ROBOTO_REGULAR_BASE64, ROBOTO_BOLD_BASE64 } from '../data/robotoFont';

/** Options controlling what goes into the exported PDF */
export interface PdfExportOptions {
  includeOverview: boolean;       // "Poglad programu" table
  includePlan: boolean;           // Week-by-week sessions & exercises
  includeWarmup: boolean;         // Warmup section in each session
  includeVolumeSummary: boolean;  // Volume per muscle table
  includeTrainingJournal: boolean; // Printable training log with empty rows
  watermarkText: string;          // Empty = no watermark
  footerBadgeText: string;        // Empty = no bottom badge
  sectionOrder?: string[];        // Custom render order (keys of this interface, excluding includeWarmup)
}

export const DEFAULT_PDF_OPTIONS: PdfExportOptions = {
  includeOverview: true,
  includePlan: true,
  includeWarmup: true,
  includeVolumeSummary: true,
  includeTrainingJournal: false,
  watermarkText: '',
  footerBadgeText: '',
  sectionOrder: ['includeOverview', 'includePlan', 'includeWarmup', 'includeVolumeSummary', 'includeTrainingJournal'],
};

/** Fallback: ensure any remaining English day names → Polish */
const EN_TO_PL_DAYS: Record<string, string> = {
  Monday: 'Poniedziałek', Tuesday: 'Wtorek', Wednesday: 'Środa',
  Thursday: 'Czwartek', Friday: 'Piątek', Saturday: 'Sobota', Sunday: 'Niedziela',
};
const plDay = (d: string) => EN_TO_PL_DAYS[d] ?? d;

function registerPolishFont(doc: jsPDF): void {
  doc.addFileToVFS('Roboto-Regular.ttf', ROBOTO_REGULAR_BASE64);
  doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  doc.addFileToVFS('Roboto-Bold.ttf', ROBOTO_BOLD_BASE64);
  doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
}

function addWatermark(doc: jsPDF, watermarkText: string, footerBadgeText: string): void {
  if (!watermarkText && !footerBadgeText) return;

  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Diagonal watermark text
    if (watermarkText) {
      doc.saveGraphicsState();
      doc.setGState(new (doc as any).GState({ opacity: 0.06 }));
      doc.setFont('Roboto', 'bold');
      doc.setFontSize(48);
      doc.setTextColor(79, 70, 229);

      const cx = pageWidth / 2;
      const cy = pageHeight / 2;
      doc.text(watermarkText, cx, cy, {
        align: 'center',
        angle: 45,
      });
      doc.restoreGraphicsState();
    }

    // Bottom badge
    if (footerBadgeText) {
      doc.setFont('Roboto', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(79, 70, 229);
      doc.text(
        footerBadgeText,
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    }
  }
}

export function exportPlanToPDF(plan: Plan, volumePerWeek?: VolumeMetrics[], options?: Partial<PdfExportOptions>): void {
  const opts: PdfExportOptions = { ...DEFAULT_PDF_OPTIONS, ...options };
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Register Roboto for Polish character support
  registerPolishFont(doc);
  doc.setFont('Roboto', 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;

  // ── Colours ─────────────────────────────
  const primary: [number, number, number] = [79, 70, 229]; // indigo-600
  const dark: [number, number, number] = [30, 27, 75];
  const gray: [number, number, number] = [100, 116, 139];

  // ── Helper ──────────────────────────────
  function addPageIfNeeded(requiredSpace: number) {
    if (y + requiredSpace > 280) {
      doc.addPage();
      y = 15;
    }
  }

  // ═══════════════════════════════════════════
  // HEADER
  // ═══════════════════════════════════════════
  doc.setFontSize(24);
  doc.setTextColor(...dark);
  doc.setFont('Roboto', 'bold');
  doc.text(plan.name || 'Program treningowy', pageWidth / 2, y, { align: 'center' });
  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(...gray);
  doc.setFont('Roboto', 'normal');
  const subtitle = `${TRAINING_GOAL_LABELS[plan.goal].toUpperCase()} • ${TRAINING_LEVEL_LABELS[plan.trainingLevel]} • ${plan.mesocycleLength} tygodni • ${plan.frequency}×/tydzień • Progresja: ${PROGRESSION_MODEL_LABELS[plan.progressionModel]}`;
  doc.text(subtitle, pageWidth / 2, y, { align: 'center' });
  y += 4;

  doc.setDrawColor(...primary);
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);
  y += 8;

  // Dispatch sections in the user-defined order
  const execOrder = (opts.sectionOrder ?? ['includeOverview', 'includePlan', 'includeVolumeSummary', 'includeTrainingJournal'])
    .filter((k) => k !== 'includeWarmup');

  for (const __sk of execOrder) {
    // ═══════════════════════════════════════════
    // OVERVIEW TABLE
    // ═══════════════════════════════════════════
    if (__sk === 'includeOverview' && opts.includeOverview) {
      doc.setFontSize(12);
      doc.setTextColor(...dark);
      doc.setFont('Roboto', 'bold');
      doc.text('Pogląd programu', 14, y);
      y += 2;

      autoTable(doc, {
        startY: y,
        margin: { left: 14 },
        theme: 'grid',
        styles: { font: 'Roboto' },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 8, font: 'Roboto', fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: dark, font: 'Roboto' },
        head: [['Parametr', 'Wartość']],
        body: [
          ['Cel', TRAINING_GOAL_LABELS[plan.goal]],
          ['Poziom', TRAINING_LEVEL_LABELS[plan.trainingLevel]],
          ['Długość mezocyklu', `${plan.mesocycleLength} tygodni`],
          ['Częstotliwość', `${plan.frequency} sesji`],
          ['Tolerancja zmęczenia', `${plan.fatigueTolerance}/10`],
          ['Model progresji', PROGRESSION_MODEL_LABELS[plan.progressionModel]],
        ],
        tableWidth: 80,
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ═══════════════════════════════════════════
    // EACH WEEK
    // ═══════════════════════════════════════════
    if (__sk === 'includePlan' && opts.includePlan) {
      for (const week of plan.weeks) {
        addPageIfNeeded(30);

        doc.setFontSize(14);
        doc.setTextColor(...primary);
        doc.setFont('Roboto', 'bold');
        doc.text(`Tydzień ${week.weekNumber}${week.deload ? ' (DELOAD)' : ''}`, 14, y);
        y += 6;

        for (const session of week.sessions) {
          // Reserve enough space so the session label never orphans at page bottom
          addPageIfNeeded(40);

          doc.setFontSize(11);
          doc.setTextColor(...dark);
          doc.setFont('Roboto', 'bold');
          doc.text(`${plDay(session.day)} - ${session.focus}`, 14, y);
          y += 2;

          // Warmup exercises
          const warmups = (opts.includeWarmup ? (session.warmupExercises || []) : []);
          if (warmups.length > 0) {
            const warmupRows = warmups.map((ex, idx) => [
              `${idx + 1}`,
              ex.name,
              `${ex.sets}×${ex.reps}`,
              formatLoad(ex.load, ex.loadType),
              ex.tempo,
              formatRest(ex.rest),
              'Rozgrzewka',
            ]);

            doc.setFontSize(8);
            doc.setTextColor(234, 88, 12); // orange-600
            doc.setFont('Roboto', 'bold');
            doc.text('🔥 Rozgrzewka', 18, y + 4);
            y += 5;

            autoTable(doc, {
              startY: y,
              margin: { left: 14 },
              theme: 'striped',
              pageBreak: 'avoid',
              rowPageBreak: 'avoid',
              styles: { font: 'Roboto' },
              headStyles: { fillColor: [255, 247, 237], textColor: [194, 65, 12] as any, fontSize: 7, fontStyle: 'bold', font: 'Roboto' },
              bodyStyles: { fontSize: 7, textColor: dark, font: 'Roboto' },
              alternateRowStyles: { fillColor: [255, 251, 245] },
              head: [['#', 'Ćwiczenie', 'Serie×Powt', 'Obciążenie', 'Tempo', 'Odpoczynek', 'Typ']],
              body: warmupRows,
              columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 50 },
                2: { cellWidth: 22 },
                3: { cellWidth: 22 },
                4: { cellWidth: 20 },
                5: { cellWidth: 18 },
                6: { cellWidth: 22 },
              },
            });

            y = (doc as any).lastAutoTable.finalY + 4;
          }

          if (session.exercises.length === 0 && warmups.length === 0) {
            doc.setFontSize(9);
            doc.setTextColor(...gray);
            doc.setFont('Roboto', 'normal');
            doc.text('Brak ćwiczeń', 18, y + 4);
            y += 8;
            continue;
          }

          if (session.exercises.length > 0 && warmups.length > 0) {
            doc.setFontSize(8);
            doc.setTextColor(...dark);
            doc.setFont('Roboto', 'bold');
            doc.text('Ćwiczenia główne', 18, y + 4);
            y += 5;
          }

          const rows = session.exercises.map((ex, idx) => [
            `${idx + 1}`,
            ex.name,
            `${ex.sets}×${ex.reps}`,
            formatLoad(ex.load, ex.loadType),
            ex.tempo,
            formatRest(ex.rest),
            EXERCISE_TAG_LABELS[ex.tag] || ex.tag,
          ]);

          autoTable(doc, {
            startY: y,
            margin: { left: 14 },
            theme: 'striped',
            pageBreak: 'avoid',
            rowPageBreak: 'avoid',
            styles: { font: 'Roboto' },
            headStyles: { fillColor: [241, 245, 249], textColor: dark, fontSize: 7, fontStyle: 'bold', font: 'Roboto' },
            bodyStyles: { fontSize: 7, textColor: dark, font: 'Roboto' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            head: [['#', 'Ćwiczenie', 'Serie×Powt', 'Obciążenie', 'Tempo', 'Odpoczynek', 'Typ']],
            body: rows,
            columnStyles: {
              0: { cellWidth: 8 },
              1: { cellWidth: 50 },
              2: { cellWidth: 22 },
              3: { cellWidth: 22 },
              4: { cellWidth: 20 },
              5: { cellWidth: 18 },
              6: { cellWidth: 22 },
            },
          });

          y = (doc as any).lastAutoTable.finalY + 6;
        }

        y += 4;
      }
    } // end if includePlan

    // ═══════════════════════════════════════════
    // VOLUME SUMMARY (if provided)
    // ═══════════════════════════════════════════
    if (__sk === 'includeVolumeSummary' && opts.includeVolumeSummary && volumePerWeek && volumePerWeek.length > 0) {
      addPageIfNeeded(40);

      doc.setFontSize(14);
      doc.setTextColor(...primary);
      doc.setFont('Roboto', 'bold');
      doc.text('Podsumowanie objętości tygodniowej (Tydzień 1)', 14, y);
      y += 2;

      const vm = volumePerWeek[0];
      const volRows = vm.muscleVolumes
        .filter((mv) => mv.directSets > 0 || mv.indirectSets > 0)
        .map((mv) => [
          MUSCLE_GROUP_LABELS[mv.muscle],
          `${mv.directSets}`,
          `${mv.indirectSets}`,
          `${mv.totalSets}`,
          `${mv.mev}`,
          `${mv.mav}`,
          `${mv.mrv}`,
          volumeStatusLabel(mv.status),
        ]);

      autoTable(doc, {
        startY: y,
        margin: { left: 14 },
        theme: 'grid',
        styles: { font: 'Roboto' },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 7, font: 'Roboto', fontStyle: 'bold' },
        bodyStyles: { fontSize: 7, textColor: dark, font: 'Roboto' },
        head: [['Mięsień', 'Bezpośr.', 'Pośr.', 'Razem', 'MEV', 'MAV', 'MRV', 'Status']],
        body: volRows,
      });

      y = (doc as any).lastAutoTable.finalY + 8;
    }

    // ═══════════════════════════════════════════
    // TRAINING JOURNAL (printable log)
    // ═══════════════════════════════════════════
    if (__sk === 'includeTrainingJournal' && opts.includeTrainingJournal) {
      // Start journal on a new page
      doc.addPage();
      y = 15;

      doc.setFontSize(20);
      doc.setTextColor(...dark);
      doc.setFont('Roboto', 'bold');
      doc.text('Dziennik treningowy', pageWidth / 2, y, { align: 'center' });
      y += 6;

      doc.setFontSize(9);
      doc.setTextColor(...gray);
      doc.setFont('Roboto', 'normal');
      doc.text(`${plan.name} - do samodzielnego uzupełniania podczas treningu`, pageWidth / 2, y, { align: 'center' });
      y += 3;
      doc.setDrawColor(...primary);
      doc.setLineWidth(0.4);
      doc.line(20, y, pageWidth - 20, y);
      y += 8;

      for (const week of plan.weeks) {
        addPageIfNeeded(30);

        // Week header
        doc.setFillColor(79, 70, 229);
        doc.roundedRect(14, y - 4, pageWidth - 28, 9, 2, 2, 'F');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.setFont('Roboto', 'bold');
        doc.text(`Tydzień ${week.weekNumber}${week.deload ? '  ·  DELOAD' : ''}`, pageWidth / 2, y + 2, { align: 'center' });
        y += 10;

        for (const session of week.sessions) {
          const allExercises = [
            ...(opts.includeWarmup ? (session.warmupExercises || []) : []),
            ...session.exercises,
          ];
          if (allExercises.length === 0) continue;

          // Estimate space: session header(12) + pre-box(18) + per exercise(7 name + 7 hdr + sets*9 + 4 gap) + post-box(38)
          const neededSpace = 12 + 18 + allExercises.reduce((sum, ex) => sum + 7 + 7 + ex.sets * 9 + 4, 0) + 38;
          addPageIfNeeded(Math.min(neededSpace, 200));

          // Session header
          doc.setFontSize(11);
          doc.setTextColor(...dark);
          doc.setFont('Roboto', 'bold');
          doc.text(`${plDay(session.day)} - ${session.focus}`, 14, y);
          y += 1;

          // Pre-session info box (2 rows)
          doc.setDrawColor(203, 213, 225);
          doc.setLineWidth(0.2);
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(14, y, pageWidth - 28, 15, 1, 1, 'FD');
          doc.setFontSize(7);
          doc.setFont('Roboto', 'normal');
          doc.setTextColor(...gray);
          doc.text('Data: _________________    Czas: od _______ do _______    Masa ciala: ________ kg', 17, y + 5);
          doc.text('Sen (1-10): _____    Stres (1-10): _____    Samopoczucie (1-10): _____    Posilek: ______ godz. przed', 17, y + 11);
          y += 18;

          for (const ex of allExercises) {
            const isWarmup = ex.tag === 'warmup';
            const setsCount = ex.sets;

            // Reserve space for: name bar(6) + col-header(7) + set rows(setsCount*9) + gap(4)
            addPageIfNeeded(6 + 7 + setsCount * 9 + 4);

            // Exercise name bar
            if (isWarmup) {
              doc.setFillColor(255, 247, 237); // orange-50
              doc.setDrawColor(251, 191, 36); // amber-400
            } else {
              doc.setFillColor(241, 245, 249); // slate-100
              doc.setDrawColor(203, 213, 225); // slate-300
            }
            doc.setLineWidth(0.3);
            doc.roundedRect(14, y - 3.5, pageWidth - 28, 7, 1, 1, 'FD');

            doc.setFontSize(8);
            doc.setFont('Roboto', 'bold');
            if (isWarmup) {
              doc.setTextColor(194, 65, 12); // orange-700
              doc.text(`[ROZGRZEWKA] ${ex.name}`, 17, y + 0.5);
            } else {
              doc.setTextColor(...dark);
              doc.text(ex.name, 17, y + 0.5);
            }

            // Planned info on right
            doc.setFont('Roboto', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(...gray);
            const plannedInfo = `Plan: ${ex.sets}×${ex.reps}  |  ${formatLoad(ex.load, ex.loadType)}  |  Odp: ${formatRest(ex.rest)}`;
            doc.text(plannedInfo, pageWidth - 16, y + 0.5, { align: 'right' });
            y += 6;

            // ── Set tracking table ──────────────────────────────────────────
            // Columns: Seria(12) | Ciezar(30) | Powt.(24) | RPE(18) | RIR(18) | Tempo(18) | Notatki(rest~62)
            const setHeaders = ['Seria', 'Ciezar [kg]', 'Powt.', 'RPE', 'RIR', 'Tempo', 'Notatki'];
            const colWidths = [12, 30, 24, 18, 18, 18, pageWidth - 28 - 12 - 30 - 24 - 18 - 18 - 18];
            const hdrH = 7;  // header row height (mm)
            const rowH = 9;  // data row height - comfortable for handwriting

            // Header row - indigo tinted background
            let xPos = 14;
            for (let c = 0; c < setHeaders.length; c++) {
              doc.setLineWidth(0.25);
              doc.setDrawColor(199, 210, 254); // indigo-200
              if (c === 0) {
                doc.setFillColor(241, 245, 249); // slate-100 for #
              } else {
                doc.setFillColor(238, 242, 255); // indigo-50
              }
              doc.rect(xPos, y, colWidths[c], hdrH, 'FD');
              doc.setFontSize(7);
              doc.setFont('Roboto', 'bold');
              doc.setTextColor(...primary);
              doc.text(setHeaders[c], xPos + colWidths[c] / 2, y + hdrH / 2 + 1.2, { align: 'center' });
              xPos += colWidths[c];
            }
            y += hdrH;

            // Empty rows for each set
            for (let s = 1; s <= setsCount; s++) {
              addPageIfNeeded(rowH + 2);
              xPos = 14;
              const rowFill: [number, number, number] = s % 2 === 0 ? [248, 250, 252] : [255, 255, 255];
              for (let c = 0; c < colWidths.length; c++) {
                doc.setLineWidth(0.15);
                doc.setDrawColor(203, 213, 225); // slate-300
                doc.setFillColor(...rowFill);
                doc.rect(xPos, y, colWidths[c], rowH, 'FD');

                // Pre-fill set number in first column
                if (c === 0) {
                  doc.setFontSize(8);
                  doc.setFont('Roboto', 'bold');
                  doc.setTextColor(148, 163, 184); // slate-400
                  doc.text(`${s}`, xPos + colWidths[c] / 2, y + rowH / 2 + 1.2, { align: 'center' });
                }

                xPos += colWidths[c];
              }
              y += rowH;
            }

            y += 4;
          }

          // ── Post-session summary box ────────────────────────────────────
          addPageIfNeeded(38);
          doc.setDrawColor(199, 210, 254); // indigo-200
          doc.setLineWidth(0.25);
          doc.setFillColor(245, 247, 255);
          doc.roundedRect(14, y, pageWidth - 28, 36, 1.5, 1.5, 'FD');

          // Title bar
          doc.setFillColor(79, 70, 229);
          doc.roundedRect(14, y, pageWidth - 28, 7, 1.5, 1.5, 'F');
          doc.rect(14, y + 3.5, pageWidth - 28, 3.5, 'F'); // mask bottom rounded corners of title
          doc.setFontSize(7.5);
          doc.setFont('Roboto', 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text('Podsumowanie sesji', 17, y + 5);
          y += 9;

          doc.setFontSize(7);
          doc.setFont('Roboto', 'normal');
          doc.setTextColor(...gray);

          // Row 1: Pompa + Zmeczenie
          doc.text('Pompa miesniowa (1-5): _____', 17, y);
          doc.text('Zmeczenie ogolne (1-10): _____', pageWidth / 2, y);
          y += 6;

          // Row 2: Kontuzje
          doc.text('Kontuzje / dyskomfort: ________________________________________________________________________________', 17, y);
          y += 6;

          // Row 3: Plan
          doc.text('Plan na nastepny trening: _____________________________________________________________________________', 17, y);
          y += 6;

          // Row 4: Notes
          doc.text('Uwagi ogolne: ________________________________________________________________________________', 17, y);
          y += 12;
        }

        y += 4;
      }
    } // end if includeTrainingJournal
  } // end for (section order loop)

  // ── Footer ─────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...gray);
    doc.text(
      `Wygenerowano przez APEX Program Builder • ${new Date().toLocaleDateString('pl-PL')}`,
      pageWidth / 2,
      285,
      { align: 'center' }
    );
    doc.text(`Strona ${i}/${pageCount}`, pageWidth - 14, 285, { align: 'right' });
  }

  // ── Watermark & certifier badge on every page ──
  addWatermark(doc, opts.watermarkText, opts.footerBadgeText);

  // ── Save ───────────────────────────────
  const filename = `${(plan.name || 'training-plan').replace(/\s+/g, '-').toLowerCase()}.pdf`;
  doc.save(filename);
}
